import {
  Viewer,
  Cartesian3,
  Matrix4,
  Matrix3,
  Quaternion,
  Cesium3DTileset,
  Transforms,
  DeveloperError,
  defined,
  Check,
  HeadingPitchRoll,
  Math as CesiumMath,
} from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import PositionRayPickerPlugin from "./PositionRayPickerPlugin";
import ScreenSpaceEventsPlugin from "./ScreenSpaceEventsPlugin";
import TranslationEditor from "components/elements/CesiumViewer/TranslationEditor";
import RotationEditor from "components/elements/CesiumViewer/RotationEditor";
import { SurfaceUtils } from "../utils/SurfaceUtils";
import { TransformUtils } from "../utils/TransformUtils";
import { ObservableProperty } from "components/elements/CesiumViewer/core/wrapper/ObservableProperty";

const noScale = new Cartesian3(1.0, 1.0, 1.0);
const transformScratch = new Matrix4();
const vectorScratch = new Cartesian3();
const scaleScratch = new Cartesian3();

const translationOffsetScratch = new Cartesian3();
const negatedCenterScratch = new Cartesian3();
const rotationMatrixScratch = new Matrix3();
const translateToOriginScratch = new Matrix4();
const translateBackScratch = new Matrix4();
const transformResultScratch = new Matrix4();
const tempMatrixScratch1 = new Matrix4();
const tempMatrixScratch2 = new Matrix4();

export enum EditorMode {
  TRANSLATION = "translation",
  ROTATION = "rotation",
}

export type TransformEditorElementState = {
  initialModelMatrix: Matrix4;
  initialBoundingSphereCenter: Cartesian3;
  modelMatrix?: Matrix4;
};

export default class TransformEditorPlugin
  implements AttachablePluginInterface
{
  private _viewer: Viewer | null = null;
  private _isActive = false;

  private _tileset: Cesium3DTileset | undefined;
  private _localToWorldTransform: Matrix4 | undefined;
  private _originalModelMatrix: Matrix4 | undefined;
  private _originalBoundingSphereCenter: Cartesian3 | undefined;
  private _previousModelMatrix: Matrix4 | undefined;
  private _previousBoundingSphereCenter: Cartesian3 | undefined;
  private _originOffset: Cartesian3;
  private _position: Cartesian3;
  private _rotationQuaternion: Quaternion;

  private _detachClickHandlerFunc: () => void;
  private _detachMoveHandlerFunc: () => void;
  private _detachLeftUpHandlerFunc: () => void;
  private _detachShiftClickHandlerFunc: () => void;
  private _removePostUpdateEvent: () => void;

  private _rotationEditor: RotationEditor;
  private _translationEditor: TranslationEditor;
  private _activeEditor?: RotationEditor | TranslationEditor;

  private _editorMode = new ObservableProperty<EditorMode | null>(null);
  private _appliedTranslation = new ObservableProperty<Cartesian3>(
    new Cartesian3(0, 0, 0),
  );
  private _appliedRotation = new ObservableProperty<HeadingPitchRoll>(
    new HeadingPitchRoll(0, 0, 0),
  );
  private _surfaceUtils: SurfaceUtils;

  constructor(
    private _screenSpaceEvents: ScreenSpaceEventsPlugin,
    private _positionPicker: PositionRayPickerPlugin,
  ) {}

  // ============================================================================
  // Public Properties (readonly)
  // ============================================================================

  /** Gets the observable applied translation */
  get appliedTranslation() {
    return this._appliedTranslation;
  }

  /** Gets the observable applied rotation */
  get appliedRotation() {
    return this._appliedRotation;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  // ============================================================================
  // Public Properties (getters/setters) - Position
  // ============================================================================

  /** Gets and sets the position */
  get position(): Cartesian3 {
    return this._position;
  }

  set position(value: Cartesian3) {
    if (!this._tileset || !this._viewer) return;
    if (Cartesian3.equals(value, this._position)) {
      return;
    }
    this._position = Cartesian3.clone(value, this._position);
    Matrix4.setTranslation(
      this._localToWorldTransform,
      this._position,
      this._localToWorldTransform,
    );
    if (this._viewer.scene.requestRenderMode) {
      this._viewer.scene.requestRender();
    }

    // Calculate the translation offset from the initial position
    const translationOffset = Cartesian3.subtract(
      value,
      this._previousBoundingSphereCenter,
      translationOffsetScratch,
    );

    // Step 2: Translate to origin (from initial bounding sphere center)
    const negatedCenter = Cartesian3.negate(
      this._previousBoundingSphereCenter,
      negatedCenterScratch,
    );
    const translateToOrigin = Matrix4.fromTranslation(
      negatedCenter,
      translateToOriginScratch,
    );

    // Step 3: Apply the translation offset
    const applyTranslation = Matrix4.fromTranslation(
      translationOffset,
      transformResultScratch,
    );

    // Step 4: Translate back to initial bounding sphere center
    const translateBack = Matrix4.fromTranslation(
      this._previousBoundingSphereCenter,
      translateBackScratch,
    );

    // Combine: translateBack * applyTranslation * translateToOrigin * initialModelMatrix
    this._tileset.modelMatrix = Matrix4.multiply(
      translateBack,
      Matrix4.multiply(
        applyTranslation,
        Matrix4.multiply(
          translateToOrigin,
          this._previousModelMatrix,
          tempMatrixScratch1,
        ),
        tempMatrixScratch2,
      ),
      this._tileset.modelMatrix,
    );

    this._updateAppliedTranslation();
  }

  /** Gets and sets the offset of the transform editor UI components from the origin */
  get originOffset(): Cartesian3 {
    return this._originOffset;
  }

  set originOffset(value: Cartesian3) {
    Check.defined("value", value);
    this._originOffset = value;

    this._translationEditor.originOffset = value;
    this._rotationEditor.originOffset = value;
  }

  // ============================================================================
  // Public Properties (getters/setters) - Rotation
  // ============================================================================

  /** Gets and sets the heading pitch roll */
  get rotationQuaternion(): Quaternion {
    return this._rotationQuaternion;
  }

  set rotationQuaternion(value: Quaternion) {
    if (Quaternion.equals(value, this._rotationQuaternion)) {
      return;
    }
    this._rotationQuaternion = Quaternion.clone(
      value,
      this._rotationQuaternion,
    );

    if (this._viewer.scene.requestRenderMode) {
      this._viewer.scene.requestRender();
    }

    // Step 1: Translate to origin (from bounding sphere center)
    const negatedCenter = Cartesian3.negate(
      this._previousBoundingSphereCenter,
      negatedCenterScratch,
    );
    const translateToOrigin = Matrix4.fromTranslation(
      negatedCenter,
      translateToOriginScratch,
    );

    // Step 2: Create rotation matrix from quaternion
    const rotationMatrix3 = Matrix3.fromQuaternion(
      value,
      rotationMatrixScratch,
    );
    const rotationMatrix = Matrix4.fromRotationTranslation(
      rotationMatrix3,
      Cartesian3.ZERO,
      transformResultScratch,
    );

    // Step 3: Translate back to bounding sphere center
    const translateBack = Matrix4.fromTranslation(
      this._previousBoundingSphereCenter,
      translateBackScratch,
    );

    // Step 4: Combine transformations: translateBack * rotation * translateToOrigin * initialModelMatrix
    this._tileset.modelMatrix = Matrix4.multiply(
      translateBack,
      Matrix4.multiply(
        rotationMatrix,
        Matrix4.multiply(
          translateToOrigin,
          this._previousModelMatrix,
          tempMatrixScratch1,
        ),
        tempMatrixScratch2,
      ),
      this._tileset.modelMatrix,
    );

    this._updateAppliedRotation();
  }

  // ============================================================================
  // Public Properties (getters/setters) - Editor Mode
  // ============================================================================

  /** Gets and sets the editor mode */
  get editorMode(): ObservableProperty<EditorMode | null> {
    return this._editorMode;
  }

  set editorMode(value: EditorMode) {
    if (this._editorMode.getSnapshot() === value) {
      return;
    }
    this._editorMode.set(value);
    if (defined(this._activeEditor)) {
      this._activeEditor.active = false;
    }
    let activeEditor: RotationEditor | TranslationEditor | undefined;
    if (value === EditorMode.ROTATION) {
      activeEditor = this._rotationEditor;
    } else if (value === EditorMode.TRANSLATION) {
      activeEditor = this._translationEditor;
    }
    if (activeEditor) {
      this._activeEditor = activeEditor;
      this._activeEditor.active = true;
    }
  }

  // ============================================================================
  // Lifecycle Methods
  // ============================================================================

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    this._surfaceUtils = new SurfaceUtils(viewer);
  }

  detachViewer() {
    this.deactivate();
    this._viewer = null;
  }

  destroy(): void {
    this.deactivate();
    this._removePostUpdateEvent?.();
    this._rotationEditor?.destroy();
    this._translationEditor?.destroy();
  }

  // ============================================================================
  // Initialization Methods
  // ============================================================================

  /**
   * Initialize the transform editor for a specific tileset
   */
  initializeForTileset(
    tileset: Cesium3DTileset,
    state?: TransformEditorElementState,
  ): void {
    if (!this._viewer) {
      throw new DeveloperError("Viewer must be set before initializing");
    }

    if (!tileset) {
      console.warn("initializeForTileset was called without a tileset");
      return;
    }

    if (!tileset.boundingSphere) {
      console.warn("Tileset boundingSphere is not available yet");
      return;
    }

    const scene = this._viewer.scene;
    const boundingSphere = tileset.boundingSphere.clone();
    const enuToWorldTransform = Transforms.eastNorthUpToFixedFrame(
      boundingSphere.center,
      scene.mapProjection.ellipsoid,
      new Matrix4(),
    );
    this._previousModelMatrix = tileset.modelMatrix.clone();
    this._previousBoundingSphereCenter = boundingSphere.center;

    let position = boundingSphere.center.clone();
    const rotationQuaternion = new Quaternion();
    const originOffset = Cartesian3.ZERO;
    const scale = Matrix4.getScale(enuToWorldTransform, new Cartesian3());

    // Safety check for if position is 0,0,0 which would be invalid
    if (
      Cartesian3.equalsEpsilon(position, Cartesian3.ZERO, CesiumMath.EPSILON10)
    ) {
      position = Cartesian3.fromDegrees(
        0,
        0,
        0,
        scene.mapProjection.ellipsoid,
        position,
      );
      Matrix4.setTranslation(
        enuToWorldTransform,
        position,
        enuToWorldTransform,
      );
    }

    const initialRadius =
      boundingSphere.radius / Cartesian3.maximumComponent(scale);

    this._tileset = tileset;
    this._originalModelMatrix =
      state?.initialModelMatrix ?? this._tileset.modelMatrix.clone();
    this._originalBoundingSphereCenter =
      state?.initialBoundingSphereCenter ?? boundingSphere.center.clone();
    this._position = position;
    this._rotationQuaternion = rotationQuaternion;

    if (this._rotationEditor) this._rotationEditor.destroy();

    if (this._translationEditor) this._translationEditor.destroy();

    const that = this;
    this._rotationEditor = new RotationEditor({
      scene: scene,
      localToWorldTransform: enuToWorldTransform,
      radius: initialRadius,
      originOffset: originOffset,
      setPosition: function (value: Cartesian3) {
        that.position = value;
      },
      setRotationQuaternion: function (value: Quaternion) {
        that.rotationQuaternion = value;
      },
    });
    this._translationEditor = new TranslationEditor({
      scene: scene,
      localToWorldTransform: enuToWorldTransform,
      radius: initialRadius,
      originOffset: originOffset,
      setPosition: function (value: Cartesian3) {
        that.position = value;
      },
    });

    this._localToWorldTransform = enuToWorldTransform;
    this._activeEditor = undefined;
    this._originOffset = originOffset;

    this.position = position;
    this.rotationQuaternion = rotationQuaternion;

    this._removePostUpdateEvent = this._viewer.scene.preUpdate.addEventListener(
      this._update.bind(this),
    );

    // Reset editor mode to force activation of the new editor instances
    this._editorMode.set(null);
    // Set default editor mode to translation
    this.editorMode = EditorMode.TRANSLATION;

    this._updateAppliedTranslation();
    this._updateAppliedRotation();
  }

  setActive(isToolActive: boolean) {
    if (!this._viewer) return;

    this._isActive = isToolActive;

    if (isToolActive) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  // ============================================================================
  // Public API Methods - Activation
  // ============================================================================

  /**
   * Activates the widget by showing the primitives and enabling mouse handlers
   */
  activate(): void {
    if (!this._viewer) return;

    this._detachClickHandlerFunc =
      this._screenSpaceEvents.leftDownEvent.addHandler(
        this._leftDown.bind(this),
      );
    this._detachMoveHandlerFunc =
      this._screenSpaceEvents.mouseMoveEvent.addHandler(
        this._mouseMove.bind(this),
      );
    this._detachLeftUpHandlerFunc =
      this._screenSpaceEvents.leftUpEvent.addHandler(this._leftUp.bind(this));
    this._detachShiftClickHandlerFunc =
      this._screenSpaceEvents.shiftLeftClickEvent.addHandler(
        this._shiftLeftClick.bind(this),
      );

    if (defined(this._activeEditor)) {
      this._activeEditor.active = true;
    }
    const scene = this._viewer.scene;
    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  }

  /**
   * Deactivates the widget by disabling mouse handlers and hiding the primitives
   */
  deactivate(): void {
    this._detachClickHandlerFunc?.();
    this._detachMoveHandlerFunc?.();
    this._detachLeftUpHandlerFunc?.();
    this._detachShiftClickHandlerFunc?.();
    this._detachClickHandlerFunc = undefined;
    this._detachMoveHandlerFunc = undefined;
    this._detachLeftUpHandlerFunc = undefined;
    this._detachShiftClickHandlerFunc = undefined;

    if (defined(this._activeEditor)) {
      this._activeEditor.active = false;
    }

    // Clear the tileset reference to prevent stale references
    this._tileset = undefined;

    const scene = this._viewer?.scene;
    if (scene?.requestRenderMode) {
      scene.requestRender();
    }
  }

  // ============================================================================
  // Public API Methods - Mode Switching
  // ============================================================================

  setModeTranslation(): void {
    this.editorMode = EditorMode.TRANSLATION;
  }

  setModeRotation(): void {
    this.editorMode = EditorMode.ROTATION;
  }

  // ============================================================================
  // Public API Methods - Position/Origin
  // ============================================================================

  /**
   * Sets the originOffset based on the Cartesian3 position in world coordinates
   */
  setOriginPosition(position: Cartesian3): void {
    Check.defined("position", position);
    let transform = Matrix4.setScale(
      this._localToWorldTransform,
      noScale,
      transformScratch,
    );
    const worldToLocalCoordinates = Matrix4.inverseTransformation(
      transform,
      transform,
    );
    const point = Matrix4.multiplyByPoint(
      worldToLocalCoordinates,
      position,
      vectorScratch,
    );
    const offset = Cartesian3.divideComponents(
      point,
      Matrix4.getScale(this._localToWorldTransform, scaleScratch),
      point,
    );

    this.originOffset = offset;
  }

  // ============================================================================
  // Public API Methods - Transform Queries
  // ============================================================================

  /**
   * Gets the applied translation by comparing current model matrix with original
   * @returns The translation offset in ENU coordinates (east, north, up)
   */
  getAppliedTranslation(): Cartesian3 {
    if (
      !this.position ||
      !this._originalBoundingSphereCenter ||
      !this._localToWorldTransform
    )
      return Cartesian3.ZERO;
    return TransformUtils.calculateTranslationInLocalSpace(
      this._position,
      this._originalBoundingSphereCenter,
      this._localToWorldTransform,
    );
  }

  getAppliedRotation(): HeadingPitchRoll {
    // Calculate rotation using the pivot center (previous or original)
    const pivotCenter =
      this._previousBoundingSphereCenter ?? this._originalBoundingSphereCenter;

    if (
      !pivotCenter ||
      !this._tileset?.modelMatrix ||
      !this._originalModelMatrix
    )
      return new HeadingPitchRoll(0, 0, 0);

    return TransformUtils.calculateRelativeRotationENU(
      this._tileset.modelMatrix,
      this._originalModelMatrix,
      pivotCenter,
    );
  }

  // ============================================================================
  // Public API Methods - Orientation
  // ============================================================================

  /**
   * Resets the transform editor axes to the default East-North-Up orientation
   */
  resetTransformOrientation(): void {
    // Reset to default ENU frame at current position
    const enuTransform = Transforms.eastNorthUpToFixedFrame(
      this._position,
      this._viewer.scene.mapProjection.ellipsoid,
      new Matrix4(),
    );

    this._localToWorldTransform = enuTransform;

    if (this._translationEditor) {
      this._translationEditor.localToWorldTransform =
        this._localToWorldTransform;
    }
    if (this._rotationEditor) {
      this._rotationEditor.localToWorldTransform = this._localToWorldTransform;
    }

    if (this._viewer.scene.requestRenderMode) {
      this._viewer.scene.requestRender();
    }
  }

  // ============================================================================
  // Private Update Methods
  // ============================================================================

  /**
   * Updates the observable applied translation property
   * @private
   */
  private _updateAppliedTranslation(): void {
    const translation = this.getAppliedTranslation();
    // Clone to ensure a new reference is created for the observable to detect the change
    this._appliedTranslation.set(Cartesian3.clone(translation));
  }

  /**
   * Updates the observable applied rotation property
   * @private
   */
  private _updateAppliedRotation(): void {
    const rotation = this.getAppliedRotation();
    // Clone to ensure a new reference is created for the observable to detect the change
    this._appliedRotation.set(
      new HeadingPitchRoll(rotation.heading, rotation.pitch, rotation.roll),
    );
  }

  private _update(): void {
    if (!this._isActive || !defined(this._activeEditor)) {
      return;
    }
    this._activeEditor.update();
  }

  // ============================================================================
  // Private Event Handlers
  // ============================================================================

  private _leftDown(click: any): void {
    if (!defined(this._activeEditor) || !defined(this._tileset)) {
      return;
    }

    // Additional safety check: ensure the tileset has a valid boundingSphere
    if (!defined(this._tileset.boundingSphere)) {
      console.warn("Tileset boundingSphere is not available yet");
      return;
    }

    this._previousModelMatrix = Matrix4.clone(this._tileset.modelMatrix);
    this._previousBoundingSphereCenter = Cartesian3.clone(
      this._tileset.boundingSphere.center,
    );
    this._activeEditor.handleLeftDown(click.position);
    const scene = this._viewer.scene;
    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  }

  private _mouseMove(movement: any): void {
    if (!defined(this._activeEditor)) {
      return;
    }
    this._activeEditor.handleMouseMove(movement.endPosition);
    const scene = this._viewer.scene;
    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  }

  private _leftUp(click: any): void {
    if (!defined(this._activeEditor)) {
      return;
    }
    this._activeEditor.handleLeftUp();
    const scene = this._viewer.scene;
    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  }

  private _shiftLeftClick(click: any): void {
    if (!this._viewer || !this._positionPicker) return;

    const ray = this._viewer.camera.getPickRay(click.position);
    const pickedObject = this._viewer.scene.pickFromRay(ray);

    if (!pickedObject?.position) return;

    const positionEvent = {
      position: pickedObject.position,
      screenPosition: click.position,
      object: pickedObject.object,
    };

    const surfaceNormal = this._surfaceUtils.getNormalDetailed(positionEvent);

    if (!surfaceNormal) {
      console.warn("Could not calculate surface normal");
      return;
    }

    this.reorientAxesToSurfaceNormal(surfaceNormal);
  }

  /**
   * Reorients the transform editor axes to be perpendicular to the given surface normal
   * @param surfaceNormalInWorld - The normal vector of the surface (in world coordinates)
   * @private
   */
  private reorientAxesToSurfaceNormal(surfaceNormalInWorld: Cartesian3): void {
    const standardEnuFrameAtTilesetPosition =
      Transforms.eastNorthUpToFixedFrame(
        this._position,
        this._viewer.scene.mapProjection.ellipsoid,
        new Matrix4(),
      );

    const enuToWorldRotation = Matrix4.getMatrix3(
      standardEnuFrameAtTilesetPosition,
      new Matrix3(),
    );
    const worldToEnuRotation = Matrix3.transpose(
      enuToWorldRotation,
      new Matrix3(),
    );

    const surfaceNormalInEnuSpace = Matrix3.multiplyByVector(
      worldToEnuRotation,
      surfaceNormalInWorld,
      new Cartesian3(),
    );
    Cartesian3.normalize(surfaceNormalInEnuSpace, surfaceNormalInEnuSpace);

    const surfaceAlignedBasisVectors =
      TransformUtils.createOrthonormalBasisFromNormal(surfaceNormalInEnuSpace);

    const basisAsRowMatrix = new Matrix3(
      surfaceAlignedBasisVectors.x.x,
      surfaceAlignedBasisVectors.x.y,
      surfaceAlignedBasisVectors.x.z,
      surfaceAlignedBasisVectors.y.x,
      surfaceAlignedBasisVectors.y.y,
      surfaceAlignedBasisVectors.y.z,
      surfaceAlignedBasisVectors.z.x,
      surfaceAlignedBasisVectors.z.y,
      surfaceAlignedBasisVectors.z.z,
    );
    const basisAsColumnMatrix = Matrix3.transpose(
      basisAsRowMatrix,
      new Matrix3(),
    );

    const rotationInLocalSpace4x4 = Matrix4.fromRotationTranslation(
      basisAsColumnMatrix,
      Cartesian3.ZERO,
      new Matrix4(),
    );

    const surfaceAlignedEnuTransform = Matrix4.multiply(
      standardEnuFrameAtTilesetPosition,
      rotationInLocalSpace4x4,
      new Matrix4(),
    );

    this._localToWorldTransform = surfaceAlignedEnuTransform;

    if (this._translationEditor) {
      this._translationEditor.localToWorldTransform =
        this._localToWorldTransform;
    }
    if (this._rotationEditor) {
      this._rotationEditor.localToWorldTransform = this._localToWorldTransform;
    }

    if (this._viewer.scene.requestRenderMode) {
      this._viewer.scene.requestRender();
    }
  }
}
